/* oxlint-disable react/purity -- lodash debounce is flagged as impure; the callback is not render */
import type { GridApi } from 'ag-grid-community'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type {
  DateFilterSelection,
  FilterManager,
  FilterModel,
} from '@/components/lemmala-design-system/table/types'
import { withoutFilterField } from '@/components/lemmala-design-system/table/utils/filter-model'

/**
 * Configuration for view definition lookup.
 * This allows the hook to remain generic while supporting view-based filtering.
 */
export interface ViewDefinitionConfig {
  getViewDefinition: (viewName: string) => {
    filterModel?: FilterModel
    [key: string]: unknown
  }
}

/**
 * Generic, centralized, debounced filter application manager.
 *
 * This hook provides the same functionality as the original useFilterManager
 * but with generic typing and configurable view definition lookup.
 *
 * Features:
 * - Uses AG Grid as the single source of truth for filters
 * - Always calls gridApi.onFilterChanged() after setting the filter model
 * - Batches frequent updates with a 300ms debounce to improve performance
 * - Supports view-based filter application via injectable view definition config
 *
 * @example
 * ```tsx
 * const filterManager = useAgFilterManager(gridApi, {
 *   getViewDefinition: (viewName) => getInventoryViewDefinition(viewName)
 * })
 *
 * // Apply filters
 * filterManager.setFilter('status', ['active', 'pending'])
 * filterManager.applyView('All')
 * ```
 */
export const useAgFilterManager = <TRowData = unknown>(
  gridApi: GridApi<TRowData> | null,
  viewConfig?: ViewDefinitionConfig,
  debounceMs = 300,
): FilterManager => {
  const [isApplying, setIsApplying] = useState(false)
  const [filterModel, setFilterModel] = useState<FilterModel>({})
  const latestModelRef = useRef<FilterModel>({})

  const applyFiltersDebounced = useMemo(
    () =>
      debounce((model: FilterModel) => {
        if (!gridApi) return
        setIsApplying(true)
        try {
          // Sanitize model keys to match existing grid columns
          const colDefs =
            (gridApi.getColumnDefs?.() as unknown as Array<{
              colId?: string
              field?: string
            }>) || []
          const allowedKeys = new Set<string>(
            colDefs.map((c) => (c.colId || c.field) as string).filter((k): k is string => !!k),
          )
          const fieldToColId = new Map<string, string>(
            colDefs
              .filter((c) => c.colId && c.field)
              .map((c) => [c.field as string, c.colId as string]),
          )

          const normalizedModel: FilterModel = {}
          Object.entries(model || {}).forEach(([key, val]) => {
            // If key is a known column id or field without explicit colId
            if (allowedKeys.has(key)) {
              normalizedModel[key] = val
              return
            }
            // If key equals a field that maps to a different explicit colId, remap
            const remapped = fieldToColId.get(key)
            if (remapped && allowedKeys.has(remapped)) {
              normalizedModel[remapped] = val
            }
            // Else drop unknown key
          })

          gridApi.setFilterModel(normalizedModel)
          gridApi.onFilterChanged()
          latestModelRef.current = normalizedModel
          setFilterModel(normalizedModel)
        } finally {
          setIsApplying(false)
        }
      }, debounceMs),
    [gridApi, debounceMs],
  )

  const setFilter = useCallback(
    (field: string, values: string[]) => {
      if (!values || values.length === 0) {
        applyFiltersDebounced(withoutFilterField(latestModelRef.current, field))
        return
      } else {
        applyFiltersDebounced({
          ...latestModelRef.current,
          [field]: { filterType: 'set', values },
        })
      }
    },
    [applyFiltersDebounced],
  )

  const setDateFilter = useCallback(
    (field: string, selection: DateFilterSelection) => {
      if ('preset' in selection) {
        applyFiltersDebounced({
          ...latestModelRef.current,
          [field]: { filterType: 'date', type: selection.preset },
        })
        return
      }
      if (!selection.dateFrom || !selection.dateTo) {
        applyFiltersDebounced(withoutFilterField(latestModelRef.current, field))
        return
      } else {
        applyFiltersDebounced({
          ...latestModelRef.current,
          [field]: {
            filterType: 'date',
            type: 'inRange',
            dateFrom: selection.dateFrom,
            dateTo: selection.dateTo,
          },
        })
      }
    },
    [applyFiltersDebounced],
  )

  const clearFilter = useCallback(
    (field: string) => {
      applyFiltersDebounced(withoutFilterField(latestModelRef.current, field))
    },
    [applyFiltersDebounced],
  )

  const clearAllFilters = useCallback(() => {
    applyFiltersDebounced({})
  }, [applyFiltersDebounced])

  const applyFilterModel = useCallback(
    (model: FilterModel) => {
      applyFiltersDebounced(model || {})
    },
    [applyFiltersDebounced],
  )

  const applyView = useCallback(
    (viewName: string) => {
      if (!viewConfig?.getViewDefinition) return

      const def = viewConfig.getViewDefinition(viewName)
      const model = (def.filterModel as FilterModel) || {}
      applyFiltersDebounced(model)
    },
    [applyFiltersDebounced, viewConfig],
  )

  // Keep internal state in sync with external grid filter changes (e.g., when views are applied)
  useEffect(() => {
    if (!gridApi) return undefined

    const syncFromGrid = () => {
      const model = (gridApi.getFilterModel() as FilterModel) || {}
      latestModelRef.current = model
      setFilterModel(model)
    }

    // Initial sync
    syncFromGrid()

    // Listen to grid filter/model updates
    gridApi.addEventListener('filterChanged', syncFromGrid)
    gridApi.addEventListener('modelUpdated', syncFromGrid)

    return () => {
      gridApi.removeEventListener('filterChanged', syncFromGrid)
      gridApi.removeEventListener('modelUpdated', syncFromGrid)
    }
  }, [gridApi])

  return {
    filterModel,
    isApplying,
    setFilter,
    setDateFilter,
    clearFilter,
    clearAllFilters,
    applyFilterModel,
    applyView,
  }
}
